import { useState, useEffect } from 'react';

import { contentfulApi } from '../api/utils/contentful';
import { AdCarousel } from '../api/contentful/models/BannerCarousel';

export interface UseAdCarouselConfig<T> {
    getEntryId: () => string | Promise<string>;
    parseEntry: (entry: unknown) => T;
}

export interface UseAdCarouselResult<T> {
    banners: T[];
    isLoading: boolean;
}

export const createAdCarouselHook = <T>(config: UseAdCarouselConfig<T>) => {
    const { getEntryId, parseEntry } = config;

    return (): UseAdCarouselResult<T> => {
        const [banners, setBanners] = useState<T[]>([]);
        const [isLoading, setIsLoading] = useState(true);

        useEffect(() => {
            let cancelled = false;
            const loadAdCarousel = async () => {
                try {
                    const entryId = await Promise.resolve(getEntryId());
                    if (cancelled) {
                        return;
                    }

                    if (!entryId) {
                        setIsLoading(false);
                        return;
                    }

                    const response = await contentfulApi.getEntry(entryId);
                    if (cancelled) {
                        return;
                    }
                    const data = response.data as { fields?: AdCarousel } | null;

                    if (data?.fields?.ads) {
                        // Filter out unpublished/draft ads. The Contentful CDA returns
                        // unresolved link stubs (without `fields`) for entries that are
                        // unpublished or scheduled to turn off. Without this filter, the
                        // parser would crash on the missing fields, causing the entire
                        // carousel to fail and fall back to the P1 Hero.
                        setBanners(
                            data.fields.ads.filter((entry: any) => entry?.fields).map(parseEntry)
                        );
                    }
                } catch (error) {
                    if (cancelled) {
                        return;
                    }
                    console.error('Failed to load AdCarousel:', error);
                } finally {
                    if (!cancelled) {
                        setIsLoading(false);
                    }
                }
            };

            loadAdCarousel();
            return () => {
                cancelled = true;
            };
        }, []);

        return { banners, isLoading };
    };
};
